import {memo, useEffect} from 'react'
import {Alert, Collapse, Snackbar} from '@mui/material'
import {useEffectEvent} from '../hooks/useEffectEvent'
import {type NotificationType, useStateNotificationStack} from '../states/NotificationStack'
import {TransitionGroup} from 'react-transition-group'

type NotificationProps = {
  notification: NotificationType
  onClose: (notification: NotificationType) => void
}

const Notification = memo(({notification, onClose}: NotificationProps) => {
  const handleClose = useEffectEvent(() => {
    onClose(notification)
  })
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose()
    }, 5000)

    return () => {
      clearTimeout(timer)
    }
  }, [handleClose])

  return <Alert
    onClose={handleClose}
    severity={notification.severity}
    variant="filled"
    sx={{width: 'fit-content', mt: 1}}
  >
    {notification.message}
  </Alert>
})

export const NotificationStackContainer = memo(() => {
  const [notificationStack, setNotificationStack] = useStateNotificationStack()
  const handleClose = useEffectEvent((notification: NotificationType) => {
    setNotificationStack((previous) => {
      delete previous[notification.key]

      return {...previous}
    })
  })

  const notifications = Object.values(notificationStack)
  if (notifications.length === 0) {
    return null
  }

  return <Snackbar
    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
    open={true}
    TransitionProps={{
      appear: false,
    }}
  >
    <TransitionGroup>
      {notifications.slice(0, 3).reverse().map((notification) => <Collapse key={notification.key}>
        <Notification
          notification={notification}
          onClose={handleClose}
        />
      </Collapse>)}
    </TransitionGroup>
  </Snackbar>
})
